/* eslint-disable no-console */
/**
 * Cart persistence and storage utilities
 */

import type { CartState, PersistedCartData, CartItemPersisted } from './cart-types'
import { createCartItemKey } from './cart-types'

export const LOCAL_CART_STORAGE_KEY = 'cart:v1'
const CURRENT_VERSION = 2
const DEBOUNCE_DELAY = 200 // ms

const TTL_MIN = 8 * 60 // 8 hours in minutes

/**
 * Debounced write function to prevent excessive localStorage writes
 */
let writeTimeout: NodeJS.Timeout | null = null

/**
 * Write cart data to localStorage with debouncing
 */
export const debouncedWriteToStorage = (state: CartState): void => {
  if (writeTimeout) {
    clearTimeout(writeTimeout)
  }

  writeTimeout = setTimeout(() => {
    try {
      const persistedData: PersistedCartData = {
        version: state.version,
        items: Object.values(state.items).map(item => ({
          policyId: item.policyId,
          productId: item.productId,
          qty: item.qty,
          isGreenpoolProduct: item.isGreenpoolProduct || false,
        })),
        userId: state.userId ?? null,
        lastUpdated: state.lastUpdated ?? null,
      }

      localStorage.setItem(LOCAL_CART_STORAGE_KEY, JSON.stringify(persistedData))
    } catch (error) {
      console.error('Failed to write cart to localStorage:', error)
    }
  }, DEBOUNCE_DELAY)
}

/**
 * Read cart data from localStorage
 */
export const readFromStorage = (): PersistedCartData | null => {
  try {
    const stored = localStorage.getItem(LOCAL_CART_STORAGE_KEY)
    if (!stored) return null
    const parsed = JSON.parse(stored)

    // Check if the data is older than 8 hours
    const now = Date.now()
    const ttl = TTL_MIN * 60 * 1000
    if (parsed.lastUpdated && now - parsed.lastUpdated > ttl) {
      console.warn('Cart data expired, resetting cart.')
      return null
    }

    return migrateCart(parsed)
  } catch (error) {
    console.error('Failed to read cart from localStorage:', error)
    return null
  }
}

/**
 * Create initial cart state from persisted data
 */
export const createInitialState = (persistedData: PersistedCartData | null): CartState => {
  if (!persistedData) {
    return {
      items: {},
      version: CURRENT_VERSION,
      isTermsAccepted: false,
      selectedAddressId: undefined,
      customAddress: undefined,
      policiesTermsStatus: {},
      userId: null, // guest by default
      lastUpdated: Date.now(),
    }
  }

  const items: Record<string, any> = {}

  for (const item of persistedData.items) {
    const key = createCartItemKey(item.policyId, item.productId)
    items[key] = {
      policyId: item.policyId,
      productId: item.productId,
      qty: item.qty,
      isGreenpoolProduct: item.isGreenpoolProduct,
      product: undefined,
      asset: undefined,
      policy: undefined,
    }
  }

  return {
    items,
    version: persistedData.version,
    isTermsAccepted: false,
    selectedAddressId: undefined,
    customAddress: undefined,
    policiesTermsStatus: {},
    userId: persistedData.userId ?? null,
    lastUpdated: persistedData.lastUpdated || Date.now(),
  }
}

/**
 * Handle cart migration for future versions
 */
export const migrateCart = (data: any): PersistedCartData => {
  // Handle invalid data
  if (!data || typeof data !== 'object') {
    return { version: CURRENT_VERSION, items: [], userId: null, lastUpdated: null }
  }

  // Handle missing version (assume v1)
  if (!data.version) {
    data.version = 1
  }

  // Handle missing items array
  if (!Array.isArray(data.items)) {
    data.items = []
  }

  // Validate and clean items
  data.items = data.items.filter(
    (item: any) =>
      item &&
      item.policyId &&
      item.productId &&
      item.qty &&
      Number(item.qty) > 0 &&
      item.isGreenpoolProduct !== undefined
  )

  // Ensure userId is present (for v1 carts)
  if (typeof data.userId === 'undefined') {
    data.userId = null
  }

  // Check if the data is older than 8 hours (2 minutes for test variable)
  const now = Date.now()
  const ttl = TTL_MIN * 60 * 1000
  if (data.lastUpdated && now - data.lastUpdated > ttl) {
    console.warn('Cart data expired, resetting cart.')
    return { version: CURRENT_VERSION, items: [], userId: null, lastUpdated: null }
  }

  switch (data.version) {
    case 1:
      // migrate to v2
      data.version = 2
      break
    case 2:
      // current
      break
    default:
      console.warn(`Unknown cart version ${data.version}, resetting cart`)
      return { version: CURRENT_VERSION, items: [], userId: null, lastUpdated: null }
  }

  return data as PersistedCartData
}

/**
 * Clear cart from localStorage
 */
export const clearStorage = (): void => {
  try {
    localStorage.removeItem(LOCAL_CART_STORAGE_KEY)
  } catch (error) {
    console.warn('Failed to clear cart from localStorage:', error)
  }
}

/**
 * Setup multi-tab sync listener
 */
export const setupStorageListener = (
  onStorageChange: (items: CartItemPersisted[]) => void
): (() => void) => {
  const handleStorageChange = (event: StorageEvent) => {
    if (event.key === LOCAL_CART_STORAGE_KEY && event.newValue !== null) {
      try {
        const parsed = JSON.parse(event.newValue)
        const migrated = migrateCart(parsed)
        onStorageChange(migrated.items)
      } catch (error) {
        console.error('Failed to parse storage change event:', error)
      }
    }
  }

  window.addEventListener('storage', handleStorageChange)

  // Return cleanup function
  return () => {
    window.removeEventListener('storage', handleStorageChange)
  }
}
